import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { AiDocumentSource } from "components/ai_chat/types";
import { CustomerDocumentDrawer } from "components/customer_documents/CustomerDocumentDrawer";
import { BuyerDocumentDrawer } from "components/document_details/BuyerDocumentDrawer";
import { BidReportEntry_Answer_Sources_Locations } from "lib/generated/app-service-gql/graphql";
import { useAiContext } from "lib/providers/AiContextProvider";
import { EventNames, useTracking } from "lib/tracking";
import { createReferenceLinkOpenedEventPayload } from "./tracking";

type Props = {
  source: AiDocumentSource;
};

export default function DocumentSource({ source }: Props) {
  const { logEvent } = useTracking();
  const { resourceId, resourceType, bidId } = useAiContext();

  const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);

  const handleSourceClick = () => {
    setIsDocumentDrawerOpen(true);
    logEvent(
      EventNames.referenceLinkOpened,
      createReferenceLinkOpenedEventPayload({
        source,
        resourceType,
        resourceId,
        bidId,
      }),
    );
  };

  const locations = source.locations || [];
  const locationCount = locations.length;
  if (source.isDeleted) {
    return (
      <Flex column gap={8}>
        <Text bold>{source.title}</Text>
        <Text description color="sysTextSecondary">
          Source deleted
        </Text>
      </Flex>
    );
  }
  return (
    <>
      <Flex column gap={8}>
        <Text bold>{source.title}</Text>
        {source.filePath && (
          <Text description color="sysTextSecondary">
            {source.filePath}
          </Text>
        )}
        <MentionsText onClick={handleSourceClick}>
          {locationCount === 1 ? "View source" : `${locationCount} mentions`}
        </MentionsText>
      </Flex>
      {source.documentType !== "BUYER_DOCUMENT" && (
        <CustomerDocumentDrawer
          source={{
            documentId: source.id,
            fileName: source.title,
            chunkNumber: source.chunkNumber,
            locations: source.locations as BidReportEntry_Answer_Sources_Locations[],
          }}
          onClose={() => setIsDocumentDrawerOpen(false)}
          isOpen={isDocumentDrawerOpen}
        />
      )}
      {source.documentType === "BUYER_DOCUMENT" && (
        <BuyerDocumentDrawer
          isOpen={isDocumentDrawerOpen}
          onClose={() => setIsDocumentDrawerOpen(false)}
          documentId={source.id}
          source={{
            documentId: source.id,
            fileName: source.title,
            chunkNumber: source.chunkNumber,
            locations: source.locations as BidReportEntry_Answer_Sources_Locations[],
          }}
        />
      )}
    </>
  );
}

const MentionsText = styled(Text)(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
  cursor: "pointer",
}));
